import { useState, type ReactNode } from "react";
import { api, ApiError, type AdminPlan, type AdminPlanStatus } from "../../api";
import { useAsync } from "../../useAsync";
import { StatusChip } from "../../components/StatusChip";
import { count, money, shortTime, DASH } from "../../format";
import { go } from "../../router";
import { startImpersonation } from "../../session";

export function AdminAccount({ id }: { id: string }) {
  const detail = useAsync(() => api.admin.account(id), [id]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setLink(null);
    try {
      await fn();
      detail.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (detail.error) return <div style={{ padding: "40px 0", color: "var(--neg)" }}>{detail.error}</div>;
  if (detail.loading || !detail.data) return <div style={{ padding: "40px 0", color: "var(--text-faint)" }}>Loading…</div>;

  const { account: a, members, products, connections, audit_log } = detail.data;
  const subscribed = !!a.stripe_subscription_id;

  return (
    <section style={{ marginTop: 18 }}>
      <button type="button" className="btn-link" onClick={() => go({ name: "admin", tab: "accounts" })}>
        ← Accounts
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginTop: 14 }}>
        <div>
          <h2 style={{ margin: 0 }}>{a.name}</h2>
          <div className="sub">{a.email}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <StatusChip
            kind={a.disabled_at ? "failing" : a.plan_status === "trialing" ? "building" : "passing"}
            label={a.disabled_at ? "Disabled" : a.plan_status === "trialing" ? "Trial" : a.plan}
          />
          <button type="button" className="btn-accent" disabled={busy || !!a.disabled_at}
                  onClick={() => act(async () => {
                    const res = await api.admin.impersonate(a.id);
                    startImpersonation({ token: res.token, account: res.account });
                    go({ name: "product", slug: null, section: "dashboard" });
                  })}>
            View as tenant
          </button>
          {a.disabled_at ? (
            <button type="button" className="btn-ghost" disabled={busy}
                    onClick={() => act(() => api.admin.enableAccount(a.id))}>
              Re-enable
            </button>
          ) : (
            <button type="button" className="btn-ghost" disabled={busy}
                    style={{ color: "var(--neg)" }}
                    onClick={() => act(() => api.admin.disableAccount(a.id))}>
              Disable
            </button>
          )}
        </div>
      </div>

      {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}
      {link && (
        <div className="banner" style={{ marginTop: 12 }}>
          <code style={{ fontSize: 11.5, wordBreak: "break-all" }}>{link}</code>
        </div>
      )}

      <div className="metrics" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", marginTop: 22 }}>
        <Stat label="Created" value={shortTime(a.created_at)} />
        <Stat label="Trial ends" value={a.trial_ends_at ? shortTime(a.trial_ends_at) : DASH} />
        <Stat label="Stripe" value={subscribed ? "subscribed" : "none"} />
        <Stat label="Role" value={a.role} />
      </div>

      <section className="panel" style={{ marginTop: 26 }}>
        <h2>Plan</h2>
        {subscribed ? (
          <p className="sub" style={{ marginTop: 8 }}>
            Live Stripe subscription — change the plan there. A manual edit here would be overwritten by the webhook.
          </p>
        ) : (
          <PlanForm
            plan={a.plan}
            status={a.plan_status}
            trialEnds={a.trial_ends_at}
            busy={busy}
            onSave={(body) => act(() => api.admin.setPlan(a.id, body))}
          />
        )}
      </section>

      <Block title="People">
        <table className="ptable">
          <thead>
            <tr><th>Person</th><th>Role</th><th>Status</th><th>Last seen</th><th></th></tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} style={{ cursor: "default" }}>
                <td>
                  <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                    <span>{m.name || m.email}</span>
                    {m.name && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{m.email}</span>}
                  </div>
                </td>
                <td><div className="cell" style={{ textTransform: "capitalize" }}>{m.member_role}</div></td>
                <td>
                  <div className="cell" style={{ textAlign: "right" }}>
                    <StatusChip
                      kind={!m.accepted_at ? "building" : m.disabled_at ? "pre-gate" : "passing"}
                      label={!m.accepted_at ? "Invited" : m.disabled_at ? "Disabled" : "Active"}
                    />
                  </div>
                </td>
                <td><div className="cell num">{m.last_seen_at ? shortTime(m.last_seen_at) : DASH}</div></td>
                <td>
                  <div className="cell" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {!m.verified_at && (
                      <button type="button" className="btn-link" disabled={busy}
                              onClick={() => act(async () => {
                                const res = await api.admin.verifyLink(m.id);
                                setLink(res.url);
                              })}>
                        Verify link
                      </button>
                    )}
                    <button type="button" className="btn-link" disabled={busy}
                            onClick={() => act(async () => {
                              const res = await api.admin.resetLink(m.id);
                              setLink(res.url);
                            })}>
                      Reset link
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Block>

      <Block title="Projects">
        <table className="ptable">
          <thead>
            <tr><th>Project</th><th>Spend 30d</th><th>Visits</th><th>Leads</th><th>Revenue 30d</th><th>Trial</th><th></th></tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} style={{ cursor: "default" }}>
                <td><div className="cell">{p.name} <span style={{ color: "var(--text-faint)" }}>{p.slug}</span></div></td>
                <td><div className="cell num">{money(p.spend_30d_cents, p.currency)}</div></td>
                <td><div className="cell num">{count(p.visits_30d)}</div></td>
                <td><div className="cell num">{count(p.leads_30d)}</div></td>
                <td><div className="cell num">{money(p.revenue_30d_cents, p.currency)}</div></td>
                <td><div className="cell num">{p.trial_ends_at ? shortTime(p.trial_ends_at) : DASH}</div></td>
                <td>
                  <div className="cell" style={{ textAlign: "right" }}>
                    <button type="button" className="btn-link" disabled={busy}
                            onClick={() => act(() => api.admin.extendProductTrial(p.id, { days: 30 }))}>
                      +30d trial
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={7}><div className="cell" style={{ color: "var(--text-4)" }}>No projects.</div></td></tr>
            )}
          </tbody>
        </table>
      </Block>

      <Block title="Connections">
        <table className="ptable">
          <thead>
            <tr><th>Project</th><th>Kind</th><th>Status</th><th>Last sync</th><th>Error</th></tr>
          </thead>
          <tbody>
            {connections.map((c) => (
              <tr key={c.id} style={{ cursor: "default" }}>
                <td><div className="cell">{c.product_slug}</div></td>
                <td><div className="cell">{c.kind}</div></td>
                <td>
                  <div className="cell" style={{ textAlign: "right" }}>
                    <StatusChip
                      kind={c.status === "active" ? "passing" : c.status === "error" ? "failing" : "building"}
                      label={c.syncing ? (c.sync_phase ?? "syncing") : c.status}
                    />
                  </div>
                </td>
                <td><div className="cell num">{c.last_sync_at ? shortTime(c.last_sync_at) : DASH}</div></td>
                <td><div className="cell" style={{ color: "var(--neg)", maxWidth: 280 }}>{c.last_error ?? DASH}</div></td>
              </tr>
            ))}
            {connections.length === 0 && (
              <tr><td colSpan={5}><div className="cell" style={{ color: "var(--text-4)" }}>No connections.</div></td></tr>
            )}
          </tbody>
        </table>
      </Block>

      <Block title="Recent audit">
        <table className="ptable">
          <thead>
            <tr><th>When</th><th>Actor</th><th>Action</th><th>Via</th></tr>
          </thead>
          <tbody>
            {audit_log.map((r, i) => (
              <tr key={`${r.created_at}-${i}`} style={{ cursor: "default" }}>
                <td><div className="cell num">{shortTime(r.created_at)}</div></td>
                <td><div className="cell">{r.actor}</div></td>
                <td><div className="cell">{r.action}</div></td>
                <td><div className="cell" style={{ color: "var(--text-faint)" }}>{r.via}</div></td>
              </tr>
            ))}
            {audit_log.length === 0 && (
              <tr><td colSpan={4}><div className="cell" style={{ color: "var(--text-4)" }}>No audit rows.</div></td></tr>
            )}
          </tbody>
        </table>
      </Block>
    </section>
  );
}

function PlanForm({
  plan, status, trialEnds, busy, onSave,
}: {
  plan: AdminPlan; status: AdminPlanStatus; trialEnds: string | null;
  busy: boolean;
  onSave: (body: { plan: AdminPlan; plan_status: AdminPlanStatus; trial_ends_at?: string | null }) => void;
}) {
  const [nextPlan, setNextPlan] = useState(plan);
  const [nextStatus, setNextStatus] = useState(status);
  const [days, setDays] = useState("30");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const trial_ends_at = nextStatus === "trialing"
          ? new Date(Date.now() + Number(days) * 86_400_000).toISOString()
          : null;
        onSave({ plan: nextPlan, plan_status: nextStatus, trial_ends_at });
      }}
      style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 14 }}
    >
      <div className="field" style={{ width: 130 }}>
        <label htmlFor="p-plan">Plan</label>
        <select id="p-plan" className="input" value={nextPlan}
                onChange={(e) => setNextPlan(e.target.value as AdminPlan)}>
          <option value="free">Free</option>
          <option value="indie">Indie</option>
          <option value="growth">Growth</option>
        </select>
      </div>
      <div className="field" style={{ width: 140 }}>
        <label htmlFor="p-status">Status</label>
        <select id="p-status" className="input" value={nextStatus}
                onChange={(e) => setNextStatus(e.target.value as AdminPlanStatus)}>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past due</option>
          <option value="canceled">Canceled</option>
        </select>
      </div>
      {nextStatus === "trialing" && (
        <div className="field" style={{ width: 110 }}>
          <label htmlFor="p-days">Trial days</label>
          <input id="p-days" className="input" type="number" min={1} max={365} value={days}
                 onChange={(e) => setDays(e.target.value)} />
        </div>
      )}
      <button type="submit" className="btn-accent" style={{ minHeight: 42 }} disabled={busy}>
        Save plan
      </button>
      {trialEnds && <span className="sub">Current trial ends {shortTime(trialEnds)}</span>}
    </form>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 30 }}>
      <h2 style={{ marginBottom: 12 }}>{title}</h2>
      <div style={{ overflowX: "auto" }}>{children}</div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div className="eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 16, letterSpacing: "-0.02em" }}>{value}</div>
    </div>
  );
}
